import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import SubscriptionFormModal from './SubscriptionFormModal';
import { Subscription } from 'src/features/subscriptions/utils/types';
import { useUpdateSubscription } from 'src/features/subscriptions/api/useUpdateSubscription';
import { LIST_SUBSCRIPTIONS_RESOURCE } from 'src/features/subscriptions/api/useListSubscriptions';
import { SUBSCRIPTION_RESOURCE } from 'src/features/subscriptions/api/useGetSubscription';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';

interface UpdateSubscriptionFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSubscription: Subscription;
    children?: React.ReactNode;
}

function UpdateSubscriptionFormModal<TData>({
    className,
    semantics,
    children,
    selectedSubscription,
}: UpdateSubscriptionFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = useUpdateSubscription({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTIONS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_RESOURCE] });
            toast({
                title: formatMessage(subscriptionsMessages.updateSubscriptionSuccess),
                description: formatMessage(subscriptionsMessages.updateSubscriptionSuccessDescription),
            });
        },
    });

    return (
        <SubscriptionFormModal
            selectedSubscription={selectedSubscription}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, id: selectedSubscription.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(subscriptionsMessages.editSubscriptionSubtitle)}
        >
            {children}
        </SubscriptionFormModal>
    );
}

export default UpdateSubscriptionFormModal;
